/**
 * Rigs — the rig / capture-arrays experience, ported from the locked mockup
 * (mockups/walkthrough: Settings · Rig · Roster · Captures). THE default since
 * 2026-06-10 (Ivin's call) — reached through IngestHome's "Rigs" card; the old
 * v1 CaptureArraysView is retired from the UI. Every write path (arm / fire /
 * serial mint / settings) reuses the EXISTING proven logic; nothing here
 * re-implements capture logic.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./rigv2.css";
import { useFirestoreDoc } from "../../../../lib/useFirestore.js";
import { assignedJobIds, isEventUser, loadAssignedJobs, rigJobPath } from "../../../../lib/eventAccount.js";
import { ingestJobList } from "../../../../lib/ingestJobs.js";
import { readPicked, useForgetIfUnusable, writePicked } from "../../../../lib/ingestList.js";
import IngestJobPicker from "../IngestJobPicker.jsx";
import QuickGuest from "./QuickGuest.jsx";
import RigScreenV2 from "./RigScreenV2.jsx";
import CapturesGallery from "./CapturesGallery.jsx";
import DeliveryReview from "./DeliveryReview.jsx";
import ClientVersionReview from "./ClientVersionReview.jsx";
import NeverEmailed from "./NeverEmailed.jsx";
import OriginalsView from "./OriginalsView.jsx";
import ProcessingPanel from "./ProcessingPanel.jsx";
import PipelineMonitor from "./PipelineMonitor.jsx";
import { canDownloadForProcessing } from "./CaptureDownload.jsx";
import RosterAdminView from "./RosterAdminView.jsx";
import JobIngestSettings from "./JobIngestSettings.jsx";
import QrReaderView from "./QrReaderView.jsx";
import UploadStatusBar from "./UploadStatusBar.jsx";
import { useModalDialog } from "./dialogFocus.js";
import { secondDisplayUrl, DISPLAY_WINDOW_NAME } from "./LastCaptureDisplay.jsx";
import { usePageIds } from "../../../../lib/feedback/pageIds.js";

const TABS = [
  { id: "settings", label: "Settings" },
  { id: "rig", label: "Rig" },
  { id: "roster", label: "Roster" },
  { id: "captures", label: "Captures" },
];
// Ivin's review of guest deliveries (DeliveryReview.jsx), and the captures
// nobody was emailed (NeverEmailed.jsx): admins only, the same as their
// callables, after Captures. Then the capture PC's originals
// (OriginalsView.jsx): what is safe to delete and what is gone, admins only,
// like its callables. Then the processing queue's controls
// (ProcessingPanel.jsx), and last the pipeline monitor (PipelineMonitor.jsx,
// reads only): admins only, as their callables.
const REVIEW_TAB = { id: "review", label: "Review" };
const NEVER_EMAILED_TAB = { id: "unsent", label: "Never emailed" };
const ORIGINALS_TAB = { id: "originals", label: "Originals" };
const PROCESSING_TAB = { id: "processing", label: "Processing" };
const MONITOR_TAB = { id: "monitor", label: "Monitor" };
export const isAdminUser = (user) => user?.role === "admin" || user?.role === "superadmin";
// An event account (docs/ops/event-role.md) writes no job settings (the rules
// refuse it), so its console has no Settings tab. Everyone else but an admin
// keeps the four tabs as they were.
export const tabsFor = (user) => {
  if (isEventUser(user)) return TABS.filter((t) => t.id !== "settings");
  return isAdminUser(user) ? [...TABS, REVIEW_TAB, NEVER_EMAILED_TAB, ORIGINALS_TAB, PROCESSING_TAB, MONITOR_TAB] : TABS;
};

function todayInNY() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

function selectDefaultCaptureSessionId(job, today = todayInNY()) {
  if (!job?.multiDay) return null;
  const sessions = Array.isArray(job.sessions) ? job.sessions : [];
  const todaysCapture = sessions.find((s) => s.date === today && s.sessionType === "capture");
  const firstCapture = sessions.find((s) => s.sessionType === "capture");
  const pick = todaysCapture ?? firstCapture ?? sessions[0] ?? null;
  return pick?.id ?? null;
}

// The second display (S8): the last capture's photographs, as its own window
// for the monitor next to the station. Named, so a second click brings the
// open window forward instead of opening another.
function openSecondDisplay(jobId) {
  window.open(secondDisplayUrl(jobId), DISPLAY_WINDOW_NAME);
}

/**
 * Whose account this shared device is using, and Sign out beside it (docs/ops/
 * password-signin.md): the next person at a greeter phone or the capture PC
 * sees at once who is signed in. On the console's two views and inside the
 * QR Reader. From 900 px up the Suite's top bar says it, beside its own Sign
 * out (AppTopbar showWho), so the console's line is hidden there (rigv2.css)
 * and Sign out shows once. Pure: exported for the SSR test.
 */
export function SignedInBar({ name, onSignOut, className = "rigv2-whoami" }) {
  if (!name) return null;
  return (
    <div className={className}>
      <span className="rigv2-whoami-text">Signed in as <strong>{name}</strong></span>
      {onSignOut && <button type="button" className="rigv2-whoami-signout" onClick={onSignOut}>Sign out</button>}
    </div>
  );
}

export const displayNameOf = (user) => {
  const name = typeof user?.name === "string" ? user.name.trim() : "";
  return name || (typeof user?.email === "string" ? user.email : "");
};

/**
 * Rigs. Staff pick a job from Ingest's one picker (IngestJobPicker.jsx); the
 * job each person picked is remembered in this browser (src/lib/ingestList.js
 * readPicked), so leaving Rigs (← Ingest, a top-bar click) and coming back
 * opens that job again, and Change job shows the picker with it marked. An
 * event account keeps its own list (EventJobPick) and nothing is remembered
 * for it, as before.
 */
export default function RigV2App({ user, onExit, onSignOut, initialJobId = null }) {
  const eventAccount = isEventUser(user);
  const [picked] = useState(() => (eventAccount || initialJobId ? null : readPicked("rigs")));
  const [jobId, setJobIdState] = useState(() => initialJobId ?? (picked?.open ? picked.jobId : null));
  const [selected, setSelected] = useState(() => initialJobId ?? picked?.jobId ?? null);
  // Opened from memory, not by a pick: if the job is gone or in the trash, forget it.
  const [restored, setRestored] = useState(() => !initialJobId && Boolean(picked?.open));
  const open = (id) => {
    setRestored(false);
    setSelected(id);
    setJobIdState(id);
    if (!eventAccount) writePicked("rigs", { jobId: id, open: true });
  };
  const changeJob = () => {
    if (!eventAccount && jobId) writePicked("rigs", { jobId, open: false });
    setRestored(false);
    setJobIdState(null);
  };
  const forget = useCallback(() => {
    writePicked("rigs", null);
    setRestored(false);
    setSelected(null);
    setJobIdState(null);
  }, []);
  useEffect(() => {
    if (initialJobId && !eventAccount) writePicked("rigs", { jobId: initialJobId, open: true });
  }, [initialJobId, eventAccount]);
  return (
    <div className="rigv2-root">
      <SignedInBar name={displayNameOf(user)} onSignOut={onSignOut} />
      {jobId ? (
        <JobShell jobId={jobId} user={user} onChangeJob={changeJob} onExit={onExit} onSignOut={onSignOut}
          onMissing={restored && !eventAccount ? forget : null} />
      ) : eventAccount ? (
        <EventJobPick onPick={open} onExit={onExit} />
      ) : (
        <IngestJobPicker screen="rigs" onPick={open} onExit={onExit} selectedId={selected}
          extra={<QuickGuest onOpen={open} />} />
      )}
    </div>
  );
}

// The jobs an event account is assigned (its token's assignedJobs), their rig
// views read one by one: the rules refuse it any list. { jobs, loading,
// failed, retry }: failed counts reads that failed (no connection), and
// retry reads them all again.
export function useAssignedJobs(enabled, { ids = assignedJobIds, load = loadAssignedJobs } = {}) {
  const [state, setState] = useState({ jobs: [], loading: Boolean(enabled), failed: 0, refused: 0 });
  const [run, setRun] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState({ jobs: [], loading: true, failed: 0, refused: 0 });
    (async () => {
      const result = await (async () => {
        try {
          return await load(await ids());
        } catch {
          return { jobs: [], failed: 1, refused: 0 };
        }
      })();
      if (!cancelled) setState({ jobs: result.jobs, loading: false, failed: result.failed, refused: result.refused ?? 0 });
    })();
    return () => { cancelled = true; };
  }, [enabled, ids, load, run]);
  return { ...state, retry: () => setRun((n) => n + 1) };
}

const jobLabel = (j) => j.displayName ?? j.id;
const pickName = (j) => j.displayName ?? j.clientName ?? j.id;

// ── An event account's job picker (unchanged) ──────────────────────────────────
// The jobs it is assigned, whatever their status (the job may be marked in
// progress on the day), from their rig views (rigJobs). Staff use Ingest's
// one picker (IngestJobPicker.jsx).
function EventJobPick({ onPick, onExit }) {
  const assigned = useAssignedJobs(true);
  const list = useMemo(
    () => ingestJobList(assigned.jobs, { now: Date.now(), eventAccount: true, label: jobLabel }),
    [assigned.jobs],
  );

  return (
    <div className="rigv2-pick">
      <div className="rigv2-pick-title">Rigs — pick a job</div>
      <div className="rigv2-pick-sub">
        Camera-array capture day.
        <button type="button" className="rigv2-exit" style={{ marginLeft: 10 }} onClick={onExit}>← Ingest</button>
      </div>
      {assigned.loading ? (
        <div className="rigv2-empty">Loading jobs…</div>
      ) : assigned.refused > 0 ? (
        <div className="rigv2-empty">
          {list.length > 0 ? "One of your jobs is not open to this account yet." : "Your job is not open to this account yet."}
          {" "}Ask Ivin, then sign out and back in.
          <button type="button" className="rigv2-exit" style={{ marginLeft: 10 }} onClick={assigned.retry}>Try again</button>
          {list.length > 0 && <JobList list={list} onPick={onPick} />}
        </div>
      ) : assigned.failed > 0 ? (
        <div className="rigv2-empty">
          {list.length > 0 ? "Some jobs could not be loaded." : "Could not load your jobs."} Check the connection.
          <button type="button" className="rigv2-exit" style={{ marginLeft: 10 }} onClick={assigned.retry}>Try again</button>
          {list.length > 0 && <JobList list={list} onPick={onPick} />}
        </div>
      ) : list.length === 0 ? (
        <div className="rigv2-empty">No job is assigned to this account yet. Ask Ivin, then sign out and back in.</div>
      ) : (
        <JobList list={list} onPick={onPick} />
      )}
    </div>
  );
}

function JobList({ list, onPick }) {
  return (
    <div className="rigv2-pick-list">
      {list.map((j) => (
        <button key={j.id} type="button" className="rigv2-pick-row" onClick={() => onPick(j.id)}>
          <span className="rigv2-pick-name">{pickName(j)}</span>
          <span className="mono rigv2-pick-id">{j.id}</span>
        </button>
      ))}
    </div>
  );
}

const MORE_MENU_ID = "rigv2-more-menu";

/**
 * The job row's overflow menu (mobile design pass item 11). On a phone the top
 * bar has room for the job name and one control, so Change job, Second display
 * and Ingest move behind this button; at desktop widths the CSS hides the whole
 * wrapper and those three stay the buttons they have always been on the single
 * row. Pure: open/closed comes in as a prop, so both states render in the test.
 * A disclosure: the button says whether the list is open and, while it is,
 * which element it controls. The shell passes the refs and onBlur that close
 * it from outside (moreShouldCloseOnBlur). Sign out comes last, set apart:
 * greeters' phones are shared (docs/ops/password-signin.md); at desktop widths
 * the Suite's top bar above the console carries it.
 */
export function TopbarMore({ open, onToggle, onChangeJob, onSecondDisplay, onIngest, onSignOut = undefined, wrapRef = null, buttonRef = null, onBlur = undefined }) {
  return (
    <div className="rigv2-more-wrap" ref={wrapRef} onBlur={onBlur}>
      <button
        type="button" className="rigv2-more" aria-label="More" ref={buttonRef}
        aria-expanded={Boolean(open)} aria-controls={open ? MORE_MENU_ID : undefined} onClick={onToggle}
      >
        ⋯
      </button>
      {open && (
        <div className="rigv2-more-menu" id={MORE_MENU_ID}>
          <button type="button" className="rigv2-more-item" onClick={onChangeJob}>Change job</button>
          <button type="button" className="rigv2-more-item" onClick={onSecondDisplay}>Second display</button>
          <button type="button" className="rigv2-more-item" onClick={onIngest}>Ingest</button>
          {onSignOut && (
            <button type="button" className="rigv2-more-item rigv2-more-signout" onClick={onSignOut}>Sign out</button>
          )}
        </div>
      )}
    </div>
  );
}

// Focus leaving the open menu for another control outside it (Tab) closes it.
// A blur with no new focus is not a reason: Safari does not focus a tapped
// button, so a tap on a menu entry blurs the ⋯ button with nothing focused,
// and closing then would drop the tap. Taps outside are the pointer handler's.
export function moreShouldCloseOnBlur(wrap, nextFocus) {
  return Boolean(nextFocus) && !(wrap && typeof wrap.contains === "function" && wrap.contains(nextFocus));
}

/**
 * The tab bar's scroll position that shows the active tab whole, with `pad`
 * pixels of the next tab beside it so the bar reads as one that scrolls; the
 * same position when it already shows. Pure: bar { scrollLeft, width,
 * scrollWidth }, tab { left, width } in the bar's own coordinates.
 */
export function scrollToShow(bar, tab, pad = 28) {
  const max = Math.max(0, (bar.scrollWidth ?? 0) - bar.width);
  let next = bar.scrollLeft;
  if (tab.left - pad < next) next = tab.left - pad;
  else if (tab.left + tab.width + pad > next + bar.width) next = tab.left + tab.width + pad - bar.width;
  return Math.min(max, Math.max(0, Math.round(next)));
}

/**
 * The console's tabs. An admin has nine; on a phone they do not fit one
 * line, so the bar (.many) scrolls sideways under a finger, with a fade at
 * the edge that has more, and the active tab is scrolled into view whenever
 * it changes (a link from the Monitor tab included). Four tabs share the
 * width as before. At desktop widths nothing scrolls.
 */
/** Which ends of the bar have tabs out of sight: "" | " more-start" | " more-end" | both (pure). */
export function edgesOf(scrollLeft, width, scrollWidth) {
  if (!(scrollWidth > width + 1)) return "";
  return `${scrollLeft > 1 ? " more-start" : ""}${scrollLeft + width < scrollWidth - 1 ? " more-end" : ""}`;
}

export function ConsoleTabs({ tabs, view, onSelect }) {
  const barRef = useRef(null);
  const [edges, setEdges] = useState("");
  const many = tabs.length > TABS.length;
  const measure = () => {
    const bar = barRef.current;
    if (bar) setEdges(edgesOf(bar.scrollLeft, bar.clientWidth, bar.scrollWidth));
  };
  // The active tab into view, and the fades: when the tab changes, and when the
  // bar's width does (a window narrowed, a phone turned).
  useEffect(() => {
    const bar = barRef.current;
    if (!bar || !many) return undefined;
    const show = () => {
      if (bar.scrollWidth > bar.clientWidth) {
        const active = bar.querySelector(".rigv2-tab.active");
        if (active) {
          const left = scrollToShow(
            { scrollLeft: bar.scrollLeft, width: bar.clientWidth, scrollWidth: bar.scrollWidth },
            { left: active.offsetLeft, width: active.offsetWidth },
          );
          if (left !== bar.scrollLeft) bar.scrollLeft = left;
        }
      }
      measure();
    };
    show();
    window.addEventListener("resize", show);
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(show) : null;
    ro?.observe(bar);
    return () => {
      window.removeEventListener("resize", show);
      ro?.disconnect();
    };
  }, [view, many]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <nav className={`rigv2-views${many ? ` many${edges}` : ""}`} ref={barRef} aria-label="Console tabs"
      onScroll={many ? measure : undefined}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`rigv2-tab ${view === t.id ? "active" : ""}`}
          aria-current={view === t.id ? "page" : undefined}
          onClick={() => onSelect(t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}

// ── Job shell: topbar (job pill + 4 tabs) + the active screen ──────────────────
function JobShell({ jobId, user, onChangeJob, onExit, onSignOut, onMissing = null }) {
  // The one job, by id; an event account reads its rig view (rigJobs), never
  // the job itself.
  const jobPath = rigJobPath(user, jobId);
  const { data: current, loading: currentLoading, error: currentError } = useFirestoreDoc(jobPath);
  // A job reopened from memory that is gone or in the trash: back to the
  // picker, on the server's word only (never a failed or offline read).
  useForgetIfUnusable({ path: jobPath, data: current, loading: currentLoading, error: currentError, onMissing });
  const tabs = tabsFor(user);
  const [view, setView] = useState("rig"); // operator-most-likely view
  const [sessionId, setSessionId] = useState(null);
  const [qrReaderOpen, setQrReaderOpen] = useState(false); // the scanning satellite
  const [moreOpen, setMoreOpen] = useState(false);         // the phone job row's overflow
  const moreWrapRef = useRef(null);
  const moreButtonRef = useRef(null);
  const qrLaunchRef = useRef(null);

  useEffect(() => {
    setSessionId(selectDefaultCaptureSessionId(current));
  }, [current?.id, current?.multiDay, current?.sessions]);

  // The overflow menu closes on a choice in it, on Escape (focus goes back to
  // ⋯), and on a tap or click anywhere outside it: phones have no Escape key,
  // and the open menu covers two tabs and the QR Reader button.
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setMoreOpen(false);
      moreButtonRef.current?.focus();
    };
    const onPointer = (e) => { if (!moreWrapRef.current?.contains(e.target)) setMoreOpen(false); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [moreOpen]);

  const fromMore = (run) => () => { setMoreOpen(false); run(); };

  const jobName = current?.displayName ?? current?.clientName ?? jobId;
  const sessions = Array.isArray(current?.sessions) ? current.sessions : [];
  const selectedSessionId = current?.multiDay ? (sessionId ?? selectDefaultCaptureSessionId(current)) : null;
  // For a feedback report from the console: the job, the tab and the capture day.
  usePageIds("rig", { jobId, view, sessionId: selectedSessionId });

  return (
    <div className="rigv2-shell">
      {/* Two lines at desktop widths (a grid in rigv2.css): Rigs, the job and
          its controls, Second display and ← Ingest; under them the tabs and the
          QR Reader. The two row wrappers are `display: contents` there; at the
          phone breakpoint they become the job row and the tab row (mobile
          design pass item 11). "NYC Suite / Ingest" is the Suite's top bar's
          to say, so the console names only the Rigs door. */}
      <header className="rigv2-topbar">
        <div className="rigv2-topbar-row1">
          <div className="rigv2-brand">
            <span className="sub">Rigs</span>
          </div>
          <div className="rigv2-job">
            <span className="rigv2-job-label">Job</span>
            <span className="rigv2-job-name" title={jobName}>{jobName}</span>
            <button type="button" className="rigv2-change-job" onClick={onChangeJob}>Change job</button>
            {current?.multiDay && sessions.length > 0 && (
              <select className="rigv2-session" aria-label="Capture session" value={selectedSessionId ?? ""} onChange={(e) => setSessionId(e.target.value || null)}>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.sessionType || "session"} · {s.date || "no date"}
                  </option>
                ))}
              </select>
            )}
          </div>
          <TopbarMore
            open={moreOpen}
            wrapRef={moreWrapRef}
            buttonRef={moreButtonRef}
            onBlur={(e) => { if (moreShouldCloseOnBlur(moreWrapRef.current, e.relatedTarget)) setMoreOpen(false); }}
            onToggle={() => setMoreOpen((v) => !v)}
            onChangeJob={fromMore(onChangeJob)}
            onSecondDisplay={fromMore(() => openSecondDisplay(jobId))}
            onIngest={fromMore(onExit)}
            onSignOut={onSignOut ? fromMore(onSignOut) : undefined}
          />
        </div>
        <div className="rigv2-topbar-row2">
          <ConsoleTabs tabs={tabs} view={view} onSelect={setView} />
          <button type="button" className="rigv2-qr-launch" ref={qrLaunchRef} onClick={() => { setMoreOpen(false); setQrReaderOpen(true); }}>⧉ QR Reader</button>
        </div>
        <button type="button" className="rigv2-qr-launch rigv2-launch-display" onClick={() => openSecondDisplay(jobId)}>⧉ Second display</button>
        <button type="button" className="rigv2-exit" onClick={onExit}>← Ingest</button>
      </header>

      {/* No inert attribute here: while the QR Reader is open, QrReaderDialog
          (useModalDialog) makes the console and the Suite around it inert. */}
      <div className="rigv2-stage">
        <TabContent view={tabs.some((t) => t.id === view) ? view : "rig"} jobId={jobId} user={user} job={current} sessionId={selectedSessionId}
          onScanToGroup={(addFor) => setQrReaderOpen({ addFor })}
          onOpenTab={(id) => { if (tabs.some((t) => t.id === id)) setView(id); }} />
      </div>

      {qrReaderOpen && (
        <QrReaderDialog launcherRef={qrLaunchRef}>
          <QrReaderView jobId={jobId} job={current} sessionId={selectedSessionId} onClose={() => setQrReaderOpen(false)}
            addFor={qrReaderOpen?.addFor ?? null}
            signedInAs={displayNameOf(user)}
            onSignOut={onSignOut ? () => { setQrReaderOpen(false); onSignOut(); } : undefined} />
        </QrReaderDialog>
      )}
    </div>
  );
}

// The QR Reader as a modal dialog (useModalDialog): while it is open the
// console and the Suite around it are inert, Tab and Shift+Tab stay inside it,
// focus that drops out of it comes back (a button that goes away, like Send to
// rig or Add a person to this capture), the armed screen's Space/Enter
// shortcut is off (modalOpen, in RigScreenV2), and on close focus goes back to
// the QR Reader button. The same for both ways in: the top bar's QR Reader
// button and the armed screen's "Scan a pass" (group check-in).
function QrReaderDialog({ launcherRef, children }) {
  const frameRef = useRef(null);
  useModalDialog(frameRef, { returnFocusRef: launcherRef });
  return (
    <div className="rigv2-satellite-scrim">
      <div className="rigv2-satellite" role="dialog" aria-modal="true" aria-label="QR Reader" tabIndex={-1} ref={frameRef}>
        {children}
      </div>
    </div>
  );
}

// onScanToGroup (group check-in): the armed screen's "Scan a pass" opens the
// QR Reader in add mode for the arm it names (qrReaderOpen { addFor: { jobId,
// leaseId } }); the top bar's QR Reader button opens it plain (true).
// An event account sends no guest email (the server refuses it
// sendGuestDelivery), so its Captures tab has no send controls. Exported for
// the SSR test.
export function TabContent({ view, jobId, user, job, sessionId, onScanToGroup, onOpenTab }) {
  // The capture PC uploader's status bar sits above the rig screen; it stays
  // hidden until an uploader has reported on this job (UploadStatusBar.jsx).
  if (view === "rig") {
    return (
      <>
        <UploadStatusBar jobId={jobId} />
        <RigScreenV2 jobId={jobId} job={job} sessionId={sessionId} onScanToGroup={onScanToGroup} />
      </>
    );
  }
  // An admin also gets each guest's delete (a guest who asked to be deleted).
  // Admins also get Download for processing (CaptureDownload.jsx).
  if (view === "captures") return <CapturesGallery jobId={jobId} canSend={!isEventUser(user)} canDeleteGuest={isAdminUser(user) && !isEventUser(user)} canOpenGuestPage={isAdminUser(user) && !isEventUser(user)} canDownload={canDownloadForProcessing(user)} />;
  // A client job's versions (SafeShare 4.5) sit above the guest models; for a guest job the list is empty and shows nothing.
  if (view === "review") return <><ClientVersionReview jobId={jobId} /><DeliveryReview jobId={jobId} /></>;
  if (view === "unsent") return <NeverEmailed jobId={jobId} />;
  if (view === "originals") return <OriginalsView jobId={jobId} />;
  if (view === "processing") return <ProcessingPanel jobId={jobId} job={job} />;
  // The monitor reads only; each line opens the tab where its action is.
  if (view === "monitor") return <PipelineMonitor jobId={jobId} onOpenTab={onOpenTab} />;
  if (view === "roster") return <RosterAdminView jobId={jobId} jobLabel={job?.displayName ?? job?.clientName ?? jobId} canImport={!isEventUser(user)} />;
  // An admin also gets the Automation card (processing, approval, automatic emails).
  if (view === "settings") return <JobIngestSettings jobId={jobId} canAutomate={isAdminUser(user) && !isEventUser(user)} />;
  return <div className="rigv2-placeholder"><h3>—</h3></div>;
}

export { selectDefaultCaptureSessionId };
